import React, { useEffect, useState } from "react";
import axios from "axios";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { getAllRolesReport } from "../api/report";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";

interface Mapping {
  id: number;
  role_id: number;
  report_type: string;
  created_at: string;
  updated_at: string | null;
}

export default function Report() {
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const res = await getAllRolesReport();
        if (!res) {
          toast.error("No data received or malformed response");
          console.warn("No data received or malformed response:", res);
          return;
        }
        setRoles(res.data.roles || {});
        setMappings(res.data.mappings || []);
      } catch (error: any) {
        console.error("Failed to fetch employee data:", error);
        if (error?.response?.status === 404) {
          toast.error("Route not found, Please try again later");
        } else if (error?.response?.status === 500) {
          toast.error("Error while fetching the employee");
        } else {
          const detail = error?.response?.data?.detail;
          if (Array.isArray(detail)) {
            detail.forEach((err) => toast.error(err.msg));
          } else {
            toast.error(
              detail?.msg || detail || "An unexpected error occurred."
            );
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMappings();
  }, []);

  const mappingByRoleId: Record<string, string[]> = mappings.reduce(
    (acc, mapping) => {
      const key = mapping.role_id.toString();
      if (!acc[key]) acc[key] = [];
      acc[key].push(mapping.report_type);
      return acc;
    },
    {} as Record<string, string[]>
  );

  return (
    <LayerAdmin>
      <div className="p-4">
        <div className="mb-4 px-5 flex items-end justify-between">
          <h3 className="text-xl font-bold text-gray-700">Role Mapping</h3>
          <button
            onClick={() => navigate(URL_MAPPING.ADDREPORT)}
            className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
          >
            Add New Role Mapping
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-2 border">Role ID</th>
                <th className="text-left p-2 border">Role Name</th>
                <th className="text-left p-2 border">Mapped Reports</th>
              </tr>
            </thead>
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={16} className="py-8">
                    <div className="flex justify-center items-center w-full">
                      <Loader />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {Object.entries(roles).map(([roleId, roleName]) => (
                  <tr key={roleId} className="border-t">
                    <td className="p-2 border">{roleId}</td>
                    <td className="p-2 border">{roleName}</td>
                    <td className="p-2 border">
                      {mappingByRoleId[roleId] ? (
                        mappingByRoleId[roleId].join(", ")
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </LayerAdmin>
  );
}
